import { useMemo, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { TRUCK_SECTION_UPDATE, generateColumn } from '../../supplementary_components/itemEditMethods';
import { addPopup, addNotification } from '../../supplementary_components/store';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { TaskList } from "../../general_components/content_management/task_list/taskList";
import { LatestLoadsSection } from "../../general_components/content_management/item_page/truck_page_components/latestLoadsSection";
import { RATE_CONF_FETCH_CONF } from "../loads/loadAddConf";
import { Form } from "../../general_components/forms/Form";
import cloneDeep from "lodash/cloneDeep";
import { AddNewTaskMethod } from "../../general_components/content_management/task_list/taskListElements/addNewTask";
import { WeeklyPerformanceSection } from '../../general_components/content_management/item_page/truck_page_components/weeklyPerformanceSection';
import { MaintenanceInvoicesSection, MaintenanceInvoicesViewAll } from '../../general_components/content_management/item_page/truck_page_components/maintenanceInvoicesSection';
import { TEMP_TRAILER_ADD_CONF } from '../trailers/trailerAddConf';
import { checkIfTruckHasOverlayingLoads } from '../loads/loadPageConf';
import { useDriversStore } from '../../main_board_components/teamCard/truckCard/hos-context';
import { useBoardStore } from '../../main_board_components/boardStore';
import { getFormattedTimePassed } from '../../main_board_components/teamCard/truckCard/truckCardComponents/currentState';
import styles from './truckPage.module.scss'
import { statusToLabelObject } from '../../main_board_components/teamCard/truckCard/truckCardComponents/driverNamesSection';
import HosGauges from './hosGauges';
import { TbSettingsDollar } from "react-icons/tb";
import { addEditAdjustmentMehtod } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { FuelCardStatusToggle } from '../fuelCards/fuelCardStatusToggle/FuelCardStatusToggle';
import { requestFuelCardStatusChange } from '../fuelCards/fuelCardStatusToggle/fuelCardStatusToggleUtils';
import { ItemOnPopup } from '../../general_components/content_management/reusableItems/itemOnPopup';

/** Only mounted when inThisStatusSince is present — subscribes to board currentTime only then. */
function HosStatusDurationSuffix({ startTimeInMs }) {
    const currentTime = useBoardStore(s => s.currentTime);
    const text = useMemo(() => {
        if (startTimeInMs == null || startTimeInMs === '') return null;
        const epoch = typeof startTimeInMs === 'number' ? startTimeInMs : Number(startTimeInMs);
        if (!Number.isFinite(epoch) || epoch <= 0) return null;
        const pastIso = DateTime.fromMillis(epoch, { zone: 'utc' }).toISO();
        const now =
            useBoardStore.getState().getLiveCurrentTime?.() ||
            (currentTime ? DateTime.fromISO(currentTime, { setZone: true }) : null);
        if (!now) return null;
        const result = getFormattedTimePassed(pastIso, now);
        if (typeof result === 'string') return result;
        return result.lbl.replace(/^For /, '').trim();
    }, [startTimeInMs, currentTime]);
    if (!text) return null;
    return <> {text}</>;
}

const DriverDataSubcomponent = ({val, name}) => {
    const driverData = val[name];
    const hosData = useDriversStore(s => s.byId[driverData ? driverData.trackingId : ""]);
    const hosObject = statusToLabelObject(hosData ? hosData.hosStatus : null);
    return driverData ? <div className={styles.driverSubComponent}>
        <div>
            <a
                href={`drivers/${driverData.id}`}
                onClick={(e) => {
                    e.preventDefault();
                    addPopup({
                        content: ItemOnPopup,
                        popupData: {
                            itemType: 'driver',
                            itemId: driverData.id,
                        },
                        customWidth: '90%',
                        customMaxWidth: '1700px',
                        customMinWidth: '880px',
                        EZCloseAllowed: true,
                    });
                }}
            >{`${driverData.firstName} ${driverData.lastName}`}</a>
            {hosObject ? <div 
                    style={hosObject.colorcode ? {backgroundColor : `rgba(${hosObject.colorcode}, 0.2)`,  color : `rgba(${hosObject.colorcode}, 1.0)`
                    } : {backgroundColor : "var(--extra-light-gray)", color:"var(--light-gray)" }}>
                        {hosObject.lbl}
                        {hosData?.inThisStatusSince != null && hosData.inThisStatusSince !== ''
                            ? <HosStatusDurationSuffix startTimeInMs={hosData.inThisStatusSince} />
                            : null}
                    </div> 
            : null}
            <div className={styles.adjustPaymentButton} onClick={()=>{
                const companyId = parseInt(localStorage.getItem("currentCompanyId"));
                addEditAdjustmentMehtod(false, ()=>{}, ()=>{}, companyId, driverData.id)
            }}>
                <TbSettingsDollar />
            </div>
        </div>
        <div>{driverData.phoneNumber}</div>
        {hosData ? <div className={styles.hosContainer}>
            <HosGauges data={hosData} />
        </div>: null}
        
    </div> : "-";
}

function GenerateDriverColumnCustomSettings(object, label, fieldName, popupMode){
    return generateColumn(object.data, true, label, [[fieldName, "firstName"], ' ', [fieldName,"lastName"]],
    {
        clickable: {id:[[fieldName, 'id']], type: "drivers/"},
        // subValuePart: popupMode && object.data[fieldName] ? object.data[fieldName].phoneNumber : null,
        subValueColumnName : fieldName,
        subValueComponent: popupMode && object.data[fieldName] ? DriverDataSubcomponent : null,
        exceptionVal:{inputName:`input[name="${fieldName==='driver1'?'driver2':'driver1'}"]`, fieldName:"id"},
        suggestionsEnabled: true,
        dataValueRequired : true,
        autoComplete:"off",
        suggestionsLink: API_ROOT+'drivers/namecontaines?name=',
        displayValueGenerator: (val)=>{return `${val["firstName"]} ${val["middleName"] ? val["middleName"] + " " :""}${val["lastName"]}`;},
        hiddenInInfoCard: fieldName === 'driver2' && !object.data[fieldName],
        availableOptionDefiningColumnName : 'available',
        twoPartValue : {
            mainPart : rawData => `${rawData["firstName"]} ${rawData["middleName"] ? rawData["middleName"] + " " :""}${rawData["lastName"]}`,
            subPart:  rawData => `${rawData['currentTruckNumber'] ? object.truckNumber === rawData['currentTruckNumber'] ? "Currently Assigned" : `(Current Truck ${rawData['currentTruckNumber']})` : ''}`,
        },
        checksMust:true,
        checks : [
            async (value, column, target) => new Promise(resolve => {
                try {
                    if(value === '' || target.getAttribute('data-value') === 'null'){
                        resolve();
                        return;
                    }
                    const parsedDriver = JSON.parse(target.getAttribute('data-value'));
                    const possibleConflictFieldVal = object.data[fieldName==='driver1'?'driver2':'driver1'];
                    if(possibleConflictFieldVal && possibleConflictFieldVal.id === parsedDriver.id){
                        target.setAttribute('data-value', ``);
                        target.value = '';
                        resolve();
                    }
                    const currentTruck = parsedDriver.currentTruckNumber;
                    if(currentTruck && currentTruck !== object.truckNumber){
                        addPopup({
                            content:ConfirmationDialogue, 
                            popupData: {
                                confirmationDialogue:true,
                                action:(postResultAction)=>{
                                    postResultAction(true);
                                },
                                cancelAction:(closePopup)=>{
                                    target.setAttribute('data-value', `${object.data[fieldName]?JSON.stringify(object.data[fieldName]):''}`);
                                    target.value = column.defaultValue();
                                    closePopup()
                                },
                                name:"Confirm",
                                formTitle:"Driver Changes",
                                submitTitle:"Continue",
                                message:`Driver will be REMOVED from truck #${currentTruck}.
                                Continue?`
                            }, 
                            postSuccessAction: ()=>{
                            }, 
                            EZCloseAllowed:false
                        })
                    }
                    resolve();
                } catch (error) {
                    console.log(error);
                }
                resolve();
            }),
        ]
    })
}
function generateDispatcherValueLabel(data){
    if(data?.dispatcher){
        return `${data?.dispatcher['firstName']} ${data?.dispatcher['lastName']}`
    }else if(data?.dispatchTeam){
        return `Team: ${data?.dispatchTeam['teamName']}` 
    }
    return "-";
}
export const generateDispatcherDisplayValueGenerator = (val)=>{
    if(val?.type === "TEAM"){
        return `Team: ${val['name']}`
    }
    return `${val['name']}`
}
const FuelCardSubcomponent = ({val, name, setData}) => {
    const fuelCardData = val[name];
    const truckId = val?.id;
    const [isActive, setIsActive] = useState(() => fuelCardData?.status === 'ACTIVE');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsActive(fuelCardData?.status === 'ACTIVE');
    }, [fuelCardData?.status]);

    if(!fuelCardData) return "-";

    const cardNumber = fuelCardData.cardNumber ?? '';

    const handleToggleChange = () => {
        if (loading) return;
        const desiredStatus = isActive ? 'INACTIVE' : 'ACTIVE';
        setLoading(true);
        requestFuelCardStatusChange({
            fuelCardId: fuelCardData.id,
            desiredStatus,
            truckId,
            onSuccess: (payload) => {
                if (payload) {
                    if (Object.keys(payload).length === 0) {
                        setData((prev) => ({ ...prev, fuelCard: null }));
                    }
                    setIsActive(payload.status === 'ACTIVE');
                    if (setData) {
                        setData((prev) => ({ ...prev, fuelCard: payload }));
                    }
                } else {
                    addNotification({ message: 'No fuel card is assigned to this truck', type: 'alert' });
                }
                setLoading(false);
            },
            onError: () => {
                addNotification({ message: 'Failed to update fuel card status', type: 'alert' });
                setLoading(false);
            },
        });
    };

    return <div className={styles.fuelCardSubcomponent}>
        <div className={`${styles.fuelCardNumber} ${fuelCardData?.blocked ? styles.blockedFuelCardNumber : ''}`} title={cardNumber}>
            <span className={styles.fuelCardNumberText}>{cardNumber}</span>
        </div>
        <FuelCardStatusToggle
            isActive={isActive}
            loading={loading}
            disabled={loading}
            onToggle={handleToggleChange}
        />
    </div>
}

export const FuelCardSection = (truckData) =>{
    // const fuelCardData = truckData['fuelCard'];
    const columnData = () => {
        return [
            {
                label: 'Fuel Card',
                name: 'fuelCard',
                subValueColumnName : "fuelCard",
                subValueComponent: FuelCardSubcomponent,
                notInAddForm:true,
                nameInAddConfForm: 'fuelCard',
                suggestionsEnabled: true,
                dataValueRequired: true,
                suggestionsLink: API_ROOT+'fuelcards/fuelcardsBySimilarCardNumber?pattern=',
                autoComplete:"off",
                afterMinimumChars:3,
                // val:truckData?.fuelCard?.cardNumber ? `${truckData["fuelCard"]["cardNumber"]}` : "-",
                displayValueGenerator:(val)=>{return `${val["cardNumber"]}`},
                defaultValue:(ref)=>{
                    const cardData =  truckData?.fuelCard;
                    if(cardData){
                        if(ref !== undefined && ref.current  !== null){
                            ref.current.setAttribute('data-edited', "false"); 
                            ref.current.setAttribute('data-value', JSON.stringify(cardData));
                        }
                        
                        return `${cardData["cardNumber"]}`;
                    }
                    return "";
                }
            },
            truckData?.fuelCard?.blocked ? null :{
                label: 'Driver Id',
                name: 'fuelCardPin',
                notInAddForm:true,
                nameInAddConfForm: 'fuelCardPin',
                autoComplete:"off",
                hiddenInInfoCard: true,
                defaultValue:()=>{
                    return truckData?.fuelCard?.pin ? `${truckData["fuelCard"]["pin"]}` : "";
                }
            }
        ]
    }
    return {
        title: 'Fuel Card',
        visibilityAndEditAccessLevel: [
            { key: 'FUEL_CARDS', level: 'MANAGE' },
        ],
        editButton : {
            text: 'Edit',
            action: (setDataMethod)=>{
                TRUCK_SECTION_UPDATE(setDataMethod, truckData.id, columnData);
            }
        },
        columns : columnData()
    }
}

export const Details = (object, popupMode) => {
    const currentDispatcher = generateDispatcherValueLabel(object.data);
    return [
        GenerateDriverColumnCustomSettings(object, 'First Driver', 'driver1', popupMode),
        GenerateDriverColumnCustomSettings(object, 'Second Driver', 'driver2', popupMode),
        generateColumn(object.data, true, 'Current Trailer', [['currentTrailer', 'trailerNumber']], {
            clickable: {id:[['currentTrailer', 'id']], type: "trailers/"},
            autoComplete:"off",
            suggestionsEnabled: true,
            dataValueRequired : true,
            
            suggestionsLink: API_ROOT+'trailers/trailersBySimilarTrailerNumber?pattern=',
            addNewButton: {label:'Add Temporary Trailer', configuration:TEMP_TRAILER_ADD_CONF},
            prefillConditioning:(val)=>{
                return {val: val, index:3};
            },
            afterMinimumChars:2,
            displayValueGenerator: (val)=>{return `${val["trailerNumber"]}`;},
            availableOptionDefiningColumnName : 'available',
            twoPartValue : {
                mainPart : rawData => `${rawData["trailerNumber"]}`,
                subPart:  rawData => `${rawData['currentTruck'] ? object.truckNumber === rawData['currentTruck'] ? "Current Trailer" : `(Attached to ${rawData['currentTruck']})` : ''}`,
            },
            checksMust:true,
            checks : [
                async (value, column, target) => new Promise(resolve => {
                    try {
                        if(value === '' || target.getAttribute('data-value') === 'null' || target.getAttribute('data-value') === "undefined"){
                            resolve();
                            return;
                        }
                        
                        const parseVal = JSON.parse(target.getAttribute('data-value'));
                        
                        const currentTruck = parseVal.currentTruck;
                        const currentTrailer = parseVal.trailerNumber;
                        
                        if(currentTruck !== null && currentTruck !== object.truckNumber){
                            sendRequest("GET", API_ROOT+`loads/loadWithTruckAndTrailerNumbers?trucknumber=${currentTruck}&trailernumber=${currentTrailer}`, null, (result)=>{
                                let CONFLICTING_LOAD_NUMBER = null;
                                for(const load of result){
                                    CONFLICTING_LOAD_NUMBER= load.loadNumber;
                                    target.setAttribute('data-value', `${object.data['currentTrailer']?JSON.stringify(object.data['currentTrailer']):''}`);
                                    target.value = column.defaultValue();
                                }
                                
                                
                                addPopup({
                                    content:ConfirmationDialogue, 
                                    popupData: {
                                        confirmationDialogue:true,
                                        oneButton: CONFLICTING_LOAD_NUMBER?true:false,
                                        action:(postResultAction)=>{
                                            postResultAction(true);
                                        },
                                        cancelAction:(closePopup)=>{
                                            target.setAttribute('data-value', `${object.data['currentTrailer']?JSON.stringify(object.data['currentTrailer']):''}`);
                                            target.value = column.defaultValue();
                                            closePopup()
                                        },
                                        name:"Confirm",
                                        formTitle:CONFLICTING_LOAD_NUMBER?"Unavailable Trailer":"Confirm Unhooking",
                                        submitTitle:CONFLICTING_LOAD_NUMBER?"OK":"Continue",
                                        message:CONFLICTING_LOAD_NUMBER?`Truck ${currentTruck} and Trailer ${currentTrailer} are assingned to the load ${CONFLICTING_LOAD_NUMBER}.`:`Trailer ${currentTrailer} will be UNHOOKED from Truck ${currentTruck}. Continue?`
                                    }, 
                                    postSuccessAction: ()=>{
                                    }, 
                                    EZCloseAllowed:false
                                })
                            })
                        }
                        resolve();
                    } catch (error) {
                        console.log(error);
                    }
                    resolve();
                }),
            ]
        }),
        generateColumn(object.data, true, 'Dispatcher', 'dispatcher',{
            suggestionsEnabled: true,
            dataValueRequired : true,
            afterMinimumChars:2,
            availableOptionDefiningColumnName : 'available',
            twoPartValue : {
                mainPart : rawData => rawData['available'] ? generateDispatcherDisplayValueGenerator(rawData) : '',
                subPart:  rawData => `${!rawData['available'] ? `${generateDispatcherDisplayValueGenerator(rawData)} (Assigned to multiple teams)` : "" }`
            },
            customProcessChanges : (field, data)=>{
                if(field.name === 'dispatcher'){
                    const val = field.dataset?.value ? JSON.parse(field.dataset.value) : null;
                    if(val?.firstName || val?.teamName)return data;
                    if(val?.type === "TEAM"){
                        data.dispatcher = null;
                        data.dispatchTeam = {id: val.id};
                    }else if (val?.type === "EMPLOYEE"){
                        data.dispatcher = {id: val.id};
                        data.dispatchTeam = null;
                    }else{
                        data["dispatcher"] = null;
                        data["dispatchTeam"] = null;
                    }
                    return data;
                }
                return data;
            },
            customDisplayFormat : ()=>{
                return currentDispatcher
            },
            
            
            suggestionsLink: API_ROOT+'employees/searchDispatchers?pattern=', // link to send request to obtain suggestions
            displayValueGenerator: generateDispatcherDisplayValueGenerator,
            // customDefaultInputValue : `${currentDispatcher !== "-" ? currentDispatcher : ""}`,
            defaultValue:(ref)=>{
                if(ref.current  !== null){
                    ref.current.setAttribute('data-edited', "false");   
                    if(object.data.dispatcher){
                        ref.current.setAttribute('data-value', JSON.stringify(object.data.dispatcher));
                        return `${object.data.dispatcher['firstName']} ${object.data.dispatcher['lastName']}`;
                    }else if(object.data.dispatchTeam){
                        ref.current.setAttribute('data-value', JSON.stringify(object.data.dispatchTeam));
                        return `Team: ${object.data.dispatchTeam['teamName']}`;
                    }
                    ref.current.setAttribute('data-value', null);
                    return "";
                }
            },
            displayValueToRawValue: (value)=>{ return value.replace('Team: ', '').trim();},
        }
        ),
    ]
}
export const ThisWeeksPerformanceBlock = (object) => {
    
    return {
        title : `This week's performance`,
        content: ()=>{
            return <WeeklyPerformanceSection
                loadsList={object.data.thisWeeksLoads}
                payoutLoads={object.data.thisWeeksDriverPayoutLoads}
                truckType={object.data.truckType}
                driver1={object.data.driver1}
                driver2={object.data.driver2}
                smallerFont={object.popup}
                canSeeRates={object.canSeeRates}
            />
        },
    }
}
export function createCheckBeforeSending(truckNumber, loadId, stops){
    
    return async (payload) => new Promise(resolve => {
        try {
            if(payload.loadType && payload.loadType === "Partial"){
                resolve({});
                return;
            }
            
            const parsedStops = stops ? stops : typeof(payload.stops) === 'string' ? JSON.parse(payload.stops) : payload.stops;
            checkIfTruckHasOverlayingLoads(truckNumber, loadId, parsedStops[0]["apptDate"], parsedStops[parsedStops.length-1]["apptDate"], null, null, (val)=>{
                if(val){
                    resolve({})
                } else {
                    resolve({cancelSubmission:true})
                }
            });
        } catch (error) {
            console.log(error);
            resolve({});
        }
    })
}
export const LatestLoadsBlock = (object) => {
    return {
        title : `Latest Loads`,
        editAccessLevel: [{ key: 'RATES' }, { key: 'INVOICES', level: 'MANAGE' }],
        editButton : {
            text: 'Add Load',
            action: (setDataMethod)=>{
                AddLoadToTruckPopup(object.data.id, object.truckNumber, null, ()=>{ sendRequest("GET", object.getLink+`${object.id}`, null, (updatedTruckData)=>{
                    setDataMethod(updatedTruckData)
        }, ()=>{})})}
        },
        content: ()=>{
            return <LatestLoadsSection
                loadsList={object.data.thisWeeksLoads}
                payoutLoads={object.data.thisWeeksDriverPayoutLoads}
                truckType={object.data.truckType}
                driver1={object.data.driver1}
                driver2={object.data.driver2}
                canSeeRates={object.canSeeRates}
            />
        },
    }
}
export const MaintenanceInvoicesBlock = (object) => {
    return {
        title: 'Latest Maintenance Invoices',
        headerAction: <MaintenanceInvoicesViewAll truckId={object.id} truckNumber={object.truckNumber} />,
        content: () => <MaintenanceInvoicesSection truckId={object.id} />,
    }
}
export const TasksBlock = (object) => {
    return {
        title : `Task List`,
        editAccessLevel: [{ key: 'RATES' }, { key: 'TRUCKS', level: 'MANAGE' }],
        editButton : {
            text: 'Edit',
            action: (setDataMethod)=>{             
                if(object.data['tasks'] && object.data['tasks'].length > 0){
                    
                    const columns = ()=>{
                        return [
                            {
                                notInAddForm:true,
                                // label: 'Stops',
                                name: "tasks",
                                type: 'taskList',
                                defaultValue:{data: object.data['tasks'], truckId: object.id, objectData: object.data, setData: setDataMethod},
                                changedTasks : [],
                                processChanges: (column, data)=>{
                                    let editedTasks = [];
                                    column.changedTasks.slice().reverse().forEach(
                                        task => {
                                            if (editedTasks.filter((obj) => obj.id === task.id).length === 0){
                                                editedTasks.push(task)
                                            }
                                        }
                                    );
                                    // editedTasks = stringifyTask(editedTasks);
                                    if(editedTasks.length > 0){
                                        editedTasks.push({"_marker":"EDITED"});
                                        data[column.name] = editedTasks
                                        return data;
                                    }
                                    return {dismissRequestWithSuccessMsg:true};                                    
                                }
                            }
                        ]
                    }
                    TRUCK_SECTION_UPDATE(setDataMethod, object.data.id, columns, "Task List Edit", null, true);
                }else{
                    const _setTasks = (tasks, loadsList) => {
                        const newData = cloneDeep(object.data);
                        newData.tasks=tasks;
                        if(loadsList){
                            newData.thisWeeksLoads = loadsList;
                        }
                        
                        setDataMethod(newData);
                    }
                    AddNewTaskMethod(null, _setTasks, object.id, 0, null)
                }
                
            }
        },
        content: (setDataMethod)=>{
            return <TaskList tasks={object.data['tasks']} truckId={object.id} currentData={object.data} setDataMethod={setDataMethod}  />//setParsedTasks={this.setParsedTasks}
        },
    }
}

export function AddLoadToTruckPopup(truckId, truckNumber, trailerData, postSuccessAction){
    const loadConfClone = cloneDeep(RATE_CONF_FETCH_CONF);
    loadConfClone.adjustingFollowingConf = (newConf)=>{
        newConf.customCheckBeforeSending = createCheckBeforeSending(truckNumber, null);
        newConf.columns.push({
            name: "currentTruck",
            hiddenField:true,
            defaultValue:()=>{
                return {id: truckId, currentTrailer:trailerData ? {id: trailerData.id} : null};
            },
            suggestionsEnabled: true,
        })
        if(trailerData){
            // newConf.columns.push({
            //     name: "currentTrailer",
            //     hiddenField:true,
            //     defaultValue:()=>{
            //         return {id: 1, trailerNumber: 'GetTrucksTrailer'};
            //     },
            //     suggestionsEnabled: true,
            // })
        }
    }                
    addPopup({content:Form, popupData:{configuration: loadConfClone}, postSuccessAction: postSuccessAction, EZCloseAllowed:false})
}
